import React, { useContext, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Link,
  InputAdornment,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import * as Yup from "yup";
import { Formik } from "formik";

import moment from "moment-timezone";

import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { postNotification } from "../../components/utilities/Notifications";
import ListResponsive from "../../components/other/ListResponsive";

import GenericPicker from "../../components/utilities/GenericPicker";
import GoogleAddressSearch from "../../components/utilities/GoogleAddressSearch";
import StoreOrderNotes from "../store/components/StoreOrderNotes";
import {
  BCPLockKeyhole,
  BCPLockKeyholeOpen,
  BCPUserMagnifyingGlass,
} from "../../components/icons/bcp/BCPIcons";
import { getData, postData } from "../../components/utilities/UtilityFunctions";

const fetchFile = async (fileId) => {
  if (!fileId) return;

  var returnObj;
  try {
    returnObj = await getData(`/files/${fileId}`, {
      queryStringParameters: {},
    });
  } catch (err) {
    console.error(err);
  }
  return returnObj;
};

const ImagePreview = (props) => {
  const { fileId, alt, outerSX } = props;
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(null);
  useEffect(() => {
    const go = async () => {
      setLoading(true);
      if (!fileId) {
        setImage(null);
        setLoading(false);
        return;
      }

      if (image) {
        if (image.id === fileId) {
          setLoading(false);
          return;
        }
        setImage(null);
      }

      try {
        var file = await fetchFile(fileId);
        if (file) {
          setImage({
            id: fileId,
            ...file,
          });
        }
      } catch (err) {
        //
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [fileId]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "50px",
        minHeight: "50px",
        maxHeight: "50px",
        p: 1,
        border: 1,
        borderColor: "divider",
        ...outerSX,
      }}
    >
      {!loading && image?.url && (
        <img
          src={image.url}
          alt={alt}
          style={{ maxWidth: "100%", maxHeight: "100%" }}
        />
      )}

      {loading && <CircularProgress size={24} />}
    </Box>
  );
};

export default function StoreAdminOrder() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  let { transactionId } = useParams();

  const [readOnly, setReadOnly] = useState(Boolean(transactionId));

  const [transaction, setTransaction] = useState([]);

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [totals, setTotals] = useState([]);

  const [saveBoxRef, saveBoxInView] = useInView({
    threshold: 0,
  });

  const theme = useTheme();

  useEffect(() => {
    const go = async () => {
      if (authContext?.authState?.identity?.isAdmin !== true) {
        navigate("/home");
        setLoading(false);
        return;
      }

      if (!transactionId) {
        setTransaction({
          transactionType: "Adjustment",
        });
        setLoading(false);
        return;
      }
      try {
        var apiTransaction = await getData(
          `/storetransactions/${transactionId}`,
          {
            queryStringParameters: {},
          }
        );

        var newTotals = [];
        var newTotal = { label: "Total", qty: 0, value: 0 };
        if (apiTransaction?.lineItems?.length) {
          apiTransaction.lineItems.forEach((item) => {
            newTotal.qty += item.qty;
            newTotal.value += item.amount;
          });
        }
        newTotals.push(newTotal);
        setTotals(newTotals);

        setTransaction(apiTransaction);
      } catch (err) {
        postNotification("error", err);
        navigate("/home");
        setLoading(false);
        return;
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, []);

  const searchUser = async (email, setFieldValue) => {
    var apiUser;
    if (email !== "") {
      setProcessing(true);
      try {
        apiUser = await getData(`/users/${email}`, {
          queryStringParameters: {},
        });
      } catch (err) {}
      setProcessing(false);
    }

    setFieldValue("userId", apiUser?.id || null);
    setFieldValue("firstName", apiUser?.firstName || "");
    setFieldValue("lastName", apiUser?.lastName || "");

    if (!apiUser?.id) {
      postNotification("error", "User not found");
    }
  };

  return (
    <>
      {!loading && (
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Formik
            initialValues={{
              transactionType: transaction?.transactionType || "",
              chargeType: transaction?.chargeType || "Loyalty Points",
              status: transaction?.status || "",
              amount: transaction?.amount || 0,
              summary: transaction?.summary || "",

              email: transaction?.email || "",
              firstName: transaction?.firstName || "",
              lastName: transaction?.lastName || "",

              deliveryAddress: {
                email: "",
                firstName: "",
                lastName: "",
                company: "",
                deliveryNotes: "",
                address1: "",
                address2: "",
                city: "",
                zip: "",
                state: "",
                country: "",
                ...transaction?.deliveryAddress,
              },
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              transactionType: Yup.string().required("Required"),
              status: Yup.string().required("Required"),

              email: Yup.string()
                .max(128)
                .email("Must be a valid email")
                .required("Required"),
              firstName: Yup.string().max(128).required("Required"),
              lastName: Yup.string().max(128).required("Required"),

              deliveryAddress: Yup.object().when("transactionType", {
                is: (transactionType) => transactionType === "Order",
                then: Yup.object().shape({
                  email: Yup.string()
                    .max(128)
                    .email("Must be a valid email")
                    .required("Required"),
                  firstName: Yup.string().max(128).required("Required"),
                  lastName: Yup.string().max(128).required("Required"),
                  address1: Yup.string().max(255).required("Required"),
                  city: Yup.string().max(255).required("Required"),
                  zip: Yup.string().max(255).required("Required"),
                  state: Yup.string().max(255).required("Required"),
                  country: Yup.string().max(255).required("Required"),
                }),
              }),
            })}
            onSubmit={async (values) => {
              try {
                setProcessing(true);

                var body = {
                  ...values,
                };

                if (body.transactionType !== "Order") {
                  delete body.deliveryAddress;
                }

                if (transaction?.id) {
                  let newTransaction = await postData(
                    `/storetransactions/${transaction.id}`,
                    {
                      body: { set: body },
                    }
                  );
                  setTransaction(newTransaction);
                  postNotification(
                    "success",
                    `${newTransaction.transactionType || "Transaction"} Updated`
                  );
                } else {
                  let newTransaction = await postData(`/storetransactions`, {
                    body: body,
                  });
                  postNotification(
                    "success",
                    `${newTransaction.transactionType || "Transaction"} Saved`
                  );
                  window.location.assign(
                    `/admin/shop/transaction/${newTransaction.id}`
                  );
                }
              } catch (err) {
                postNotification("error", err);
                setProcessing(false);
              }

              setProcessing(false);
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue,
              setValues,
            }) => (
              <form onSubmit={handleSubmit} noValidate>
                <Grid container sx={{ gap: 2 }}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      p: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      boxShadow: 1,
                      backgroundColor: "background.secondary",
                    }}
                  >
                    <Typography variant="h3">
                      {transaction.transactionType || "Transaction"} #
                      {transaction?.orderId?.toUpperCase()}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sx={{
                      p: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      boxShadow: 1,
                      backgroundColor: "background.secondary",
                    }}
                  >
                    <Typography variant="h5">
                      <Button
                        color="primary"
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                          setReadOnly(!readOnly);
                        }}
                        endIcon={
                          readOnly ? (
                            <BCPLockKeyhole outlined />
                          ) : (
                            <BCPLockKeyholeOpen outlined />
                          )
                        }
                      >
                        {readOnly ? "Locked" : "Unlocked"}
                      </Button>
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={values.transactionType === "Order" ? 6 : 12}
                    sx={{
                      p: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      boxShadow: 1,
                      backgroundColor: "background.secondary",
                    }}
                  >
                    <Typography variant="h4" gutterBottom>
                      General
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField
                          label="Email"
                          name="email"
                          value={values.email}
                          variant="outlined"
                          fullWidth
                          margin="normal"
                          disabled={readOnly}
                          required
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="Search"
                                  edge="end"
                                  color="primary"
                                  disabled={readOnly}
                                  onClick={async () => {
                                    await searchUser(
                                      values.email,
                                      setFieldValue
                                    );
                                  }}
                                >
                                  <BCPUserMagnifyingGlass />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          onBlur={async (e) => {
                            handleBlur(e);
                            await searchUser(e.target.value, setFieldValue);
                          }}
                          onChange={(e) => {
                            const trimmedString = e.target.value
                              .trim()
                              .toLowerCase();
                            setFieldValue("email", trimmedString);
                          }}
                          error={Boolean(touched.email && errors.email)}
                          helperText={touched.email && errors.email}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          name="firstName"
                          label="First Name"
                          value={values.firstName}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.firstName && errors.firstName)}
                          helperText={touched.firstName && errors.firstName}
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="lastName"
                          label="Last Name"
                          value={values.lastName}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.lastName && errors.lastName)}
                          helperText={touched.lastName && errors.lastName}
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        {transaction?.id ? (
                          <TextField
                            label="Transaction Type"
                            value={transaction?.transactionType}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            disabled
                            required
                          />
                        ) : (
                          <GenericPicker
                            name="transactionType"
                            label="Transaction Type"
                            value={values.transactionType}
                            handleChange={handleChange}
                            inclAny={false}
                            required
                            disabled={readOnly}
                            list={["Order", "Adjustment", "Judging", "Refund"]}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <GenericPicker
                          name="status"
                          label="Status"
                          value={values.status}
                          handleChange={handleChange}
                          inclAny={false}
                          required
                          disabled={readOnly}
                          list={[
                            "Pending",
                            "Processing",
                            "Being Packed",
                            "Packed",
                            "Shipped",
                            "Completed",
                            "Cancelled",
                            "Refunded",
                          ]}
                        />
                      </Grid>

                      {transaction?.id && (
                        <>
                          <Grid item xs={12}>
                            <TextField
                              name="created_at"
                              label="Created At"
                              variant="outlined"
                              fullWidth
                              margin="normal"
                              type="datetime-local"
                              defaultValue={moment(
                                transaction?.created_at
                              ).format("YYYY-MM-DDTHH:mm")}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              sx={{
                                colorScheme:
                                  theme?.palette?.mode === "dark"
                                    ? "dark"
                                    : null,
                              }}
                              disabled
                              required
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              name="updated_at"
                              label="Last Update"
                              variant="outlined"
                              fullWidth
                              margin="normal"
                              type="datetime-local"
                              defaultValue={moment(
                                transaction?.updated_at
                              ).format("YYYY-MM-DDTHH:mm")}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              sx={{
                                colorScheme:
                                  theme?.palette?.mode === "dark"
                                    ? "dark"
                                    : null,
                              }}
                              disabled
                              required
                            />
                          </Grid>
                        </>
                      )}

                      {(values.transactionType === "Adjustment" ||
                        values.transactionType === "Judging" ||
                        values.transactionType === "Refund") && (
                        <Grid item xs={12}>
                          <TextField
                            name="amount"
                            label="Amount"
                            value={values.amount}
                            variant="outlined"
                            fullWidth
                            multiline
                            type="number"
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(touched.amount && errors.amount)}
                            helperText={touched.amount && errors.amount}
                            disabled={readOnly}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <TextField
                          name="summary"
                          label="Summary"
                          value={values.summary}
                          variant="outlined"
                          fullWidth
                          multiline
                          rows={3}
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.summary && errors.summary)}
                          helperText={touched.summary && errors.summary}
                          disabled={readOnly}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {values.transactionType === "Order" && (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{
                        p: 2,
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        borderRadius: 1,
                        boxShadow: 1,
                        backgroundColor: "background.secondary",

                        maxWidth: [
                          "calc(100% ) !important",
                          "calc(100% ) !important",
                          "calc(50% - 18px) !important",
                        ],
                        minWidth: [
                          "calc(100% ) !important",
                          "calc(100% ) !important",
                          "calc(50% - 18px) !important",
                        ],
                      }}
                    >
                      <Typography variant="h4" gutterBottom>
                        Shipping
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <TextField
                            name="deliveryAddress.email"
                            label="Email"
                            value={values.deliveryAddress?.email}
                            variant="outlined"
                            fullWidth
                            required
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.deliveryAddress?.email &&
                                errors.deliveryAddress?.email
                            )}
                            helperText={
                              touched.deliveryAddress?.email &&
                              errors.deliveryAddress?.email
                            }
                            disabled={readOnly}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            name="deliveryAddress.firstName"
                            label="First Name"
                            value={values.deliveryAddress?.firstName}
                            variant="outlined"
                            fullWidth
                            required
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.deliveryAddress?.firstName &&
                                errors.deliveryAddress?.firstName
                            )}
                            helperText={
                              touched.deliveryAddress?.firstName &&
                              errors.deliveryAddress?.firstName
                            }
                            disabled={readOnly}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            name="deliveryAddress.lastName"
                            label="Last Name"
                            value={values.deliveryAddress?.lastName}
                            variant="outlined"
                            fullWidth
                            required
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.deliveryAddress?.lastName &&
                                errors.deliveryAddress?.lastName
                            )}
                            helperText={
                              touched.deliveryAddress?.lastName &&
                              errors.deliveryAddress?.lastName
                            }
                            disabled={readOnly}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="deliveryAddress.company"
                            label="Company (optional)"
                            value={values.deliveryAddress?.company}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.deliveryAddress?.company &&
                                errors.deliveryAddress?.company
                            )}
                            helperText={
                              touched.deliveryAddress?.company &&
                              errors.deliveryAddress?.company
                            }
                            disabled={readOnly}
                          />
                        </Grid>
                        {!readOnly && (
                          <Grid item xs={12}>
                            <GoogleAddressSearch
                              label="Address Lookup"
                              setLocationData={(e) => {
                                var address1 = `${
                                  e.streetNum !== undefined ? e.streetNum : ""
                                } ${
                                  e.streetName !== undefined ? e.streetName : ""
                                }`.trim();
                                delete e.streetNum;
                                delete e.streetName;

                                setValues({
                                  ...values,
                                  deliveryAddress: {
                                    ...values.deliveryAddress,
                                    ...e,
                                    address1: address1,
                                  },
                                });
                              }}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <TextField
                            name="deliveryAddress.address1"
                            label="Address Line 1"
                            value={values.deliveryAddress?.address1}
                            variant="outlined"
                            fullWidth
                            required
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.deliveryAddress?.address1 &&
                                errors.deliveryAddress?.address1
                            )}
                            helperText={
                              touched.deliveryAddress?.address1 &&
                              errors.deliveryAddress?.address1
                            }
                            disabled={readOnly}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="deliveryAddress.address2"
                            label="Address Line 2"
                            value={values.deliveryAddress?.address2}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.deliveryAddress?.address2 &&
                                errors.deliveryAddress?.address2
                            )}
                            helperText={
                              touched.deliveryAddress?.address2 &&
                              errors.deliveryAddress?.address2
                            }
                            disabled={readOnly}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="deliveryAddress.city"
                            label="City"
                            value={values.deliveryAddress?.city}
                            variant="outlined"
                            fullWidth
                            required
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.deliveryAddress?.city &&
                                errors.deliveryAddress?.city
                            )}
                            helperText={
                              touched.deliveryAddress?.city &&
                              errors.deliveryAddress?.city
                            }
                            disabled={readOnly}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="deliveryAddress.state"
                            label="State"
                            value={values.deliveryAddress?.state}
                            variant="outlined"
                            fullWidth
                            required
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.deliveryAddress?.state &&
                                errors.deliveryAddress?.state
                            )}
                            helperText={
                              touched.deliveryAddress?.state &&
                              errors.deliveryAddress?.state
                            }
                            disabled={readOnly}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="deliveryAddress.zip"
                            label="Zip / Post Code"
                            value={values.deliveryAddress?.zip}
                            variant="outlined"
                            fullWidth
                            required
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.deliveryAddress?.zip &&
                                errors.deliveryAddress?.zip
                            )}
                            helperText={
                              touched.deliveryAddress?.zip &&
                              errors.deliveryAddress?.zip
                            }
                            disabled={readOnly}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="deliveryAddress.country"
                            label="Country"
                            value={values.deliveryAddress?.country}
                            variant="outlined"
                            fullWidth
                            required
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.deliveryAddress?.country &&
                                errors.deliveryAddress?.country
                            )}
                            helperText={
                              touched.deliveryAddress?.country &&
                              errors.deliveryAddress?.country
                            }
                            disabled={readOnly}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            name="deliveryAddress.deliveryNotes"
                            value={values.deliveryAddress?.deliveryNotes}
                            label="Delivery Notes"
                            variant="outlined"
                            fullWidth
                            onBlur={handleBlur}
                            onChange={handleChange}
                            multiline
                            rows={5}
                            error={Boolean(
                              touched.deliveryAddress?.deliveryNotes &&
                                errors.deliveryAddress?.deliveryNotes
                            )}
                            helperText={
                              touched.deliveryAddress?.deliveryNotes &&
                              errors.deliveryAddress?.deliveryNotes
                            }
                            disabled={readOnly}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}

                  {transaction?.id && (
                    <Grid
                      item
                      xs={12}
                      sx={{
                        p: 2,
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        borderRadius: 1,
                        boxShadow: 1,
                        backgroundColor: "background.secondary",
                      }}
                    >
                      <StoreOrderNotes
                        isAdmin
                        transaction={transaction}
                        setTransaction={setTransaction}
                      />
                    </Grid>
                  )}
                  {transaction?.lineItems?.length > 0 && (
                    <Grid
                      item
                      xs={12}
                      sx={{
                        p: 2,
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        borderRadius: 1,
                        boxShadow: 1,
                        backgroundColor: "background.secondary",
                      }}
                    >
                      <ListResponsive
                        originalList={transaction?.lineItems || []}
                        rowProps={{
                          headings: <RowItem heading />,
                          mapFunction: (row, index) => (
                            <RowItem key={index} row={row} />
                          ),
                        }}
                      />

                      {totals?.length > 0 &&
                        totals.map((total, index) => {
                          return <RowItem key={index} total={total} />;
                        })}
                    </Grid>
                  )}

                  <Grid
                    item
                    xs={12}
                    sx={{
                      p: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      boxShadow: 1,
                      backgroundColor: "background.secondary",
                    }}
                  >
                    <Grid item xs={12} sx={{ textAlign: "right" }}>
                      <Button
                        ref={saveBoxRef}
                        color="success"
                        variant="contained"
                        type="submit"
                        disabled={readOnly}
                      >
                        Save
                      </Button>
                    </Grid>
                    {!saveBoxInView && (
                      <Box
                        sx={{
                          position: "fixed",
                          zIndex: 999,
                          p: 2,
                          bgcolor: "background.paper",
                          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                          boxShadow: 3,
                          bottom: 0,
                          left: [0, 0, 0, 256],
                          width: ["100%", "100%", "100%", "calc(100% - 256px)"],
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            fullWidth
                            color="success"
                            variant="contained"
                            type="submit"
                            disabled={readOnly}
                          >
                            Save
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Container>
      )}
      {(loading || processing) && <ModalLoadingFull />}
    </>
  );
}

const RowItem = (props) => {
  const { heading, row, total } = props;

  return (
    <Grid
      item
      xs={12}
      sx={{
        p: 1,
        mb: 1,
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: 1,
        boxShadow: 1,
      }}
    >
      <Grid container spacing={1}>
        <Grid item xs={6}>
          {heading ? (
            <Typography variant="body1">Product</Typography>
          ) : total ? (
            <Typography variant="body1">{total.label || ""}</Typography>
          ) : (
            <Grid container>
              <Grid item xs sx={{ maxWidth: ["50px", "70px"] }}>
                <ImagePreview
                  fileId={row?.product?.imageFileId}
                  alt={row?.product.name}
                />
              </Grid>
              <Grid item xs>
                <Link
                  href={`/shop/product/${row?.product.id}`}
                  underline="none"
                >
                  <Typography variant="body1">{row?.product.name}</Typography>
                </Link>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item xs={2}>
          {heading ? (
            <Typography variant="body1">Price</Typography>
          ) : total ? (
            <></>
          ) : (
            <Typography variant="body1">{row.price} LP</Typography>
          )}
        </Grid>
        <Grid
          item
          xs={2}
          sx={{
            textAlign: "center",
          }}
        >
          {heading ? (
            <Typography variant="body1">Quantity</Typography>
          ) : total ? (
            <Typography variant="body1">{total.qty}</Typography>
          ) : (
            <Typography variant="body1">{row.qty}</Typography>
          )}
        </Grid>
        <Grid
          item
          xs={2}
          sx={{
            textAlign: "right",
          }}
        >
          {heading ? (
            <Typography variant="body1">Subtotal</Typography>
          ) : total ? (
            <Typography variant="body1">{total.value} LP</Typography>
          ) : (
            <Typography variant="body1">{row.amount} LP</Typography>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
